import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import fetchData from "../../functions/fetchData";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import { Chart, registerables } from 'chart.js';

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { getHeaders } from "../../utility/getHeader";

// Register Chart.js components
Chart.register(...registerables);

export default function Expenses() {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSummaryTable, setShowSummaryTable] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Print ref
  const printRef = useRef();
  
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    invdte: format(new Date(), "yyyy-MM-dd"),
    userid: "7483",
    head: "",
    subhead: "",
    ref: "",
    payment_mode: "cash",
    bank: "0",
    prebalance: "0",
    credit: "0",
    vat: "0",
    Disc: "0",
    debit: "0",
    balance: "0",
    remark: "",
    includeVat: false
  });

  const [heads, setHeads] = useState([]);
  const [subheads, setSubheads] = useState([]);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    head: "",
    subhead: "",
    paymentMode: "",
    amountOperator: "greater", // greater or less
    amountValue: ""
  });
  const [filteredData, setFilteredData] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Table columns configuration - Memoized to prevent re-creation
  const columns = useMemo(() => [
    { label: "Date", field: "date", align: "left" },
    { label: "Head", field: "head", align: "left" },
    { label: "Sub Head", field: "subhead", align: "left" },
    { label: "Invoice/Ref", field: "ref", align: "left" },
    { label: "Payment Mode", field: "payment_mode", align: "left" },
    { label: "Credit", field: "credit", align: "left" },
    { label: "Discount", field: "Disc", align: "left" },
    { label: "Debit", field: "debit", align: "left" },
    { label: "Balance", field: "balance", align: "left" },
    { label: "Remark", field: "remark", align: "left" },
    { label: "Actions", field: "actions", align: "center" }
  ], []);

  // Pagination calculations
  const paginationData = useMemo(() => {
    const dataToUse = filteredData.length > 0 ? filteredData : data;
    const totalPages = Math.ceil(dataToUse.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = dataToUse.slice(indexOfFirstItem, indexOfLastItem);
    
    return {
      currentItems,
      totalPages,
      totalItems: dataToUse.length,
      startItem: indexOfFirstItem + 1,
      endItem: Math.min(indexOfLastItem, dataToUse.length)
    };
  }, [data, filteredData, currentPage, itemsPerPage]);

  // Calculate closing balance from filtered or full data
  const closingBalance = useMemo(() => {
    const dataToUse = filteredData.length > 0 ? filteredData : data;
    let maxBalance = 0;
    
    dataToUse.forEach(record => {
      const balance = parseFloat(record.balance) || 0;
      if (balance > maxBalance) {
        maxBalance = balance;
      }
    });
    
    return maxBalance;
  }, [data, filteredData]);

  const loadExpensesData = useCallback(() => {
    try {
      fetchData("expenses", (newData) => {
        setData(newData);
        // Reset to first page when data changes
        if (currentPage > Math.ceil(newData.length / itemsPerPage)) {
          setCurrentPage(1);
        }
      }, "id", {});
    } catch (error) {
      console.error("Error loading expenses data:", error);
    }
  }, [currentPage, itemsPerPage]);

  // Pagination helper functions
  const goToPage = useCallback((pageNumber) => {
    const dataToUse = filteredData.length > 0 ? filteredData : data;
    setCurrentPage(Math.max(1, Math.min(pageNumber, Math.ceil(dataToUse.length / itemsPerPage))));
  }, [data, filteredData, itemsPerPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    const dataToUse = filteredData.length > 0 ? filteredData : data;
    setCurrentPage(Math.ceil(dataToUse.length / itemsPerPage));
  }, [data, filteredData, itemsPerPage]);

  const goToNextPage = useCallback(() => {
    const dataToUse = filteredData.length > 0 ? filteredData : data;
    setCurrentPage(prev => Math.min(prev + 1, Math.ceil(dataToUse.length / itemsPerPage)));
  }, [data, filteredData, itemsPerPage]);

  const goToPrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);

  const loadHeadsData = useCallback(() => {
    try {
      fetchData("expense_ledger", setHeads, "id", {});
    } catch (error) {
      console.error("Error loading heads data:", error);
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    loadExpensesData();
    loadHeadsData();
  }, [loadExpensesData, loadHeadsData]);

  // Auto-calculate totals when financial fields change
  useEffect(() => {
    const credit = parseFloat(formData.credit) || 0;
    const vat = parseFloat(formData.vat) || 0;
    const disc = parseFloat(formData.Disc) || 0;
    const debit = parseFloat(formData.debit) || 0;
    const prebalance = parseFloat(formData.prebalance) || 0;
    
    let vatAmount = vat;
    let shouldUpdateVat = false;
    
    if (formData.includeVat && credit > 0) {
      const calculatedVat = (credit * 7) / 100;
      if (Math.abs(calculatedVat - vat) > 0.01) { // Only update if significantly different
        vatAmount = calculatedVat;
        shouldUpdateVat = true;
      }
    }
    
    const balance = prebalance + credit + vatAmount - disc - debit;
    const newBalance = balance.toFixed(2);
    
    // Only update if balance has actually changed to prevent infinite loops
    if (formData.balance !== newBalance || shouldUpdateVat) {
      setFormData(prev => {
        const updates = {
          balance: newBalance
        };
        
        if (shouldUpdateVat) {
          updates.vat = vatAmount.toFixed(2);
        }
        
        return { ...prev, ...updates };
      });
    }
  }, [formData.credit, formData.vat, formData.Disc, formData.debit, formData.prebalance, formData.includeVat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch previous balance for selected subhead - Optimized
  const fetchSubheadBalance = useCallback(async (subhead) => {
    if (!subhead) return;
    
    setIsLoadingBalance(true);
    
    try {
      const response = await axios.get(`/api/expenses/last`, {
        params: { subhead: subhead },
        ...getHeaders()
      });
      
      if (response.data.success && response.data.data && response.data.data.record) {
        const latestRecord = response.data.data.record;
        const balance = parseFloat(latestRecord.amount) || 0;
        setFormData(prev => ({
          ...prev,
          prebalance: balance.toString()
        }));
        if (toast && typeof toast.success === 'function') {
          toast.success(`Previous balance loaded: ฿${balance}`);
        }
      } else {
        setFormData(prev => ({
          ...prev,
          prebalance: "0"
        }));
        if (toast && typeof toast.info === 'function') {
          toast.info(`No previous records found for "${subhead}". Balance set to ฿0.`);
        }
      }
    } catch (error) {
      console.error("Error fetching subhead balance:", error);
      
      if (error.response && error.response.status === 404) {
        setFormData(prev => ({ ...prev, prebalance: "0" }));
        if (toast && typeof toast.info === 'function') {
          toast.info(`No previous records found for "${subhead}". Balance set to ฿0.`);
        }
      } else {
        setFormData(prev => ({ ...prev, prebalance: "0" }));
        if (toast && typeof toast.error === 'function') {
          toast.error("Error fetching previous balance. Balance set to ฿0.");
        }
      }
    } finally {
      setIsLoadingBalance(false);
    }
  }, []);

  // Handle form input changes - Optimized
  const handleInputChange = useCallback((e) => {
    try {
      const { name, value, type, checked } = e.target;
      
      console.log('Input change event:', { 
        name, 
        value, 
        type, 
        checked, 
        target: e.target
      }); // Enhanced debug logging
      
      setFormData(prev => {
        console.log('State update - prev:', prev, 'new value:', value);
        const updated = {
          ...prev,
          [name]: type === 'checkbox' ? checked : value,
        };
        console.log('Updated formData:', updated);
        return updated;
      });
      
      // Fetch balance when subhead changes
      if (name === 'subhead' && value) {
        fetchSubheadBalance(value);
      }
    } catch (error) {
      console.error('Error in handleInputChange:', error);
    }
  }, [fetchSubheadBalance]);

  // Debug the form data state
  useEffect(() => {
    console.log('Current formData state:', formData);
  }, [formData]);

  // Handle head selection change
  const handleHeadChange = (e) => {
    const selectedHead = e.target.value;
    console.log('Head change:', selectedHead); // Debug logging
    
    setFormData(prev => ({
      ...prev,
      head: selectedHead,
      subhead: "",
      prebalance: "0"
    }));
    
    if (selectedHead) {
      fetchData("expense_subhead", setSubheads, "id", { mainhead: selectedHead });
    } else {
      setSubheads([]);
    }
  };

  // Handle form submission - Optimized
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      await axios.post("/insertdata/expenses", formData, getHeaders());
      if (toast && typeof toast.success === 'function') {
        toast.success("Expense entry saved successfully!");
      }

      // Reset form
      setFormData({
        date: format(new Date(), "yyyy-MM-dd"),
        invdte: format(new Date(), "yyyy-MM-dd"),
        userid: "7483",
        head: "",
        subhead: "",
        ref: "",
        payment_mode: "cash",
        bank: "0",
        prebalance: "0",
        credit: "0",
        vat: "0",
        Disc: "0",
        debit: "0",
        balance: "0",
        remark: "",
        includeVat: false
      });

      // Close modal and refresh data
      setShowModal(false);
      loadExpensesData();
      
    } catch (error) {
      console.error(error);
      if (toast && typeof toast.error === 'function') {
        toast.error("Failed to save expense entry.");
      }
    }
  }, [formData, loadExpensesData]);

  // Open modal for new expense - Optimized
  const openNewExpenseModal = useCallback(() => {
    const initialData = {
      date: format(new Date(), "yyyy-MM-dd"),
      invdte: format(new Date(), "yyyy-MM-dd"),
      userid: "7483",
      head: "",
      subhead: "",
      ref: "",
      payment_mode: "cash",
      bank: "0",
      prebalance: "0",
      credit: "0",
      vat: "0",
      Disc: "0",
      debit: "0",
      balance: "0",
      remark: "",
      includeVat: false
    };
    
    console.log('Opening new expense modal with initial data:', initialData);
    setFormData(initialData);
    setShowModal(true);
  }, []);

  // Close modal - Optimized
  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // Handle edit record - Optimized
  const handleEditRecord = useCallback((record) => {
    setEditingRecord(record);
    setFormData({
      date: record.date || format(new Date(), "yyyy-MM-dd"),
      invdte: record.invdte || format(new Date(), "yyyy-MM-dd"),
      userid: record.userid || "7483",
      head: record.head || "",
      subhead: record.subhead || "",
      ref: record.ref || "",
      payment_mode: record.payment_mode || "cash",
      bank: record.bank || "0",
      prebalance: record.prebalance || "0",
      credit: record.credit || "0",
      vat: record.vat || "0",
      Disc: record.Disc || "0",
      debit: record.debit || "0",
      balance: record.balance || "0",
      remark: record.remark || "",
      includeVat: false
    });
    setShowEditModal(true);
  }, []);

  // Handle delete record - Optimized
  const handleDeleteRecord = useCallback(async (recordId) => {
    if (window.confirm("Are you sure you want to delete this expense record?")) {
      try {
        await axios.delete(`/deletedata/expenses/${recordId}`, getHeaders());
        if (toast && typeof toast.success === 'function') {
          toast.success("Expense record deleted successfully!");
        }
        loadExpensesData(); // Refresh the data
      } catch (error) {
        console.error("Error deleting record:", error);
        if (toast && typeof toast.error === 'function') {
          toast.error("Failed to delete expense record.");
        }
      }
    }
  }, [loadExpensesData]);

  // Print Payment Voucher - Thai Legal Compliant
  const printPaymentVoucher = useCallback(async (record) => {
    try {
      // Fetch company info
      const companyInfoResponse = await fetchData("bill_format", null, "id", {});
      const companyInfo = companyInfoResponse && companyInfoResponse.length > 0 ? companyInfoResponse[0] : {};

      // Format date in Thai format
      const voucherDate = record.date ? new Date(record.date).toLocaleDateString('th-TH', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : '-';

      const invoiceDate = record.invdte ? new Date(record.invdte).toLocaleDateString('th-TH', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : '-';

      // Number to Thai text conversion
      const numberToThaiText = (num) => {
        const ones = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
        const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];
        
        if (num === 0) return 'ศูนย์บาท';
        
        let result = '';
        const numStr = Math.floor(num).toString();
        const len = numStr.length;
        
        for (let i = 0; i < len; i++) {
          const digit = parseInt(numStr[i]);
          const pos = len - i - 1;
          
          if (digit === 0) continue;
          
          if (pos === 1) {
            if (digit === 1) {
              result += 'สิบ';
            } else if (digit === 2) {
              result += 'ยี่สิบ';
            } else {
              result += ones[digit] + 'สิบ';
            }
          } else if (pos === 0 && digit === 1 && len > 1) {
            result += 'เอ็ด';
          } else {
            result += ones[digit];
            if (pos > 0 && pos < positions.length) result += positions[pos];
          }
        }
        
        return result + 'บาทถ้วน';
      };

      const amountInWords = numberToThaiText(parseFloat(record.debit || 0));

      // Create print window content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset=\"UTF-8\">
          <title>Payment Voucher - ${record.id}</title>
          <style>
            @media print {
              @page {
                size: A5 portrait;
                margin: 3mm;
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Sarabun', 'Tahoma', sans-serif;
              font-size: 11px;
              line-height: 1.4;
              color: #000;
              background: #f5f5f5;
              padding: 5px;
            }
            
            .voucher-container {
              max-width: 100%;
              margin: 0;
              margin-left: 35px;
              margin-right: 25px;
              border: 2px solid #333;
              padding: 6px;
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            }
            
            .header {
              text-align: center;
              margin-bottom: 8px;
              border-bottom: 2px solid #333;
              padding-bottom: 6px;
              background: linear-gradient(to bottom, #e3f2fd, #bbdefb);
              padding: 6px;
              border-radius: 4px 4px 0 0;
              position: relative;
            }
            
            .header-logo {
              position: absolute;
              top: 8px;
              right: 8px;
              width: 60px;
              height: auto;
            }
            
            .header h1 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 3px;
              color: #0d47a1;
            }
            
            .header h2 {
              font-size: 14px;
              margin-bottom: 5px;
            }
            
            .company-info {
              text-align: center;
              font-size: 9px;
              margin-bottom: 3px;
              color: #333;
            }
            
            .voucher-title {
              text-align: center;
              font-size: 16px;
              font-weight: bold;
              margin: 8px 0;
              text-decoration: underline;
              background: linear-gradient(to right, #fff3e0, #ffe0b2);
              padding: 6px;
              border-radius: 4px;
            }
            
            .voucher-details {
              margin-bottom: 10px;
              background: #ffffff;
              padding: 6px;
              border-radius: 4px;
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 6px;
            }
            
            .detail-row {
              display: flex;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .detail-label {
              width: 70px;
              font-weight: bold;
              color: #424242;
              font-size: 9px;
            }
            
            .detail-value {
              flex: 1;
              border-bottom: 1px dotted #666;
              padding-left: 6px;
            }
            
            .amount-section {
              margin: 10px 0;
              border: 1px solid #333;
              padding: 8px;
              background: linear-gradient(135deg, #fff9c4 0%, #fff59d 100%);
              border-radius: 4px;
            }
            
            .amount-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 6px;
              font-size: 12px;
            }
            
            .amount-row.total {
              font-size: 14px;
              font-weight: bold;
              border-top: 2px solid #333;
              padding-top: 8px;
              margin-top: 8px;
              color: #c62828;
            }
            
            .amount-in-words {
              margin-top: 6px;
              padding: 6px;
              border: 1px solid #333;
              background-color: #fff;
              border-radius: 3px;
              font-size: 11px;
            }
            
            .signature-section {
              margin-top: 12px;
              display: flex;
              justify-content: space-between;
            }
            
            .signature-box {
              width: 45%;
              text-align: center;
              background: #f0f0f0;
              padding: 5px;
              border-radius: 4px;
            }
            
            .signature-line {
              border-top: 1px solid #333;
              margin-top: 30px;
              padding-top: 4px;
              font-size: 10px;
            }
            
            .footer {
              margin-top: 10px;
              text-align: center;
              font-size: 9px;
              color: #666;
              border-top: 1px solid #ccc;
              padding-top: 6px;
              background: #fafafa;
              padding: 4px;
              border-radius: 0 0 4px 4px;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
              font-size: 11px;
            }
            
            th, td {
              border: 1px solid #333;
              padding: 6px;
              text-align: left;
            }
            
            th {
              background: linear-gradient(to bottom, #90caf9, #64b5f6);
              font-weight: bold;
              color: #0d47a1;
            }
            
            tbody tr {
              background: #ffffff;
            }
            
            tbody tr:nth-child(even) {
              background: #f5f5f5;
            }
            
            .text-right {
              text-align: right;
            }
            
            .text-center {
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class=\"voucher-container\">
            <!-- Header -->
            <div class=\"header\">
              <img src=\"/logo.png\" alt=\"Jannaat Logo\" class=\"header-logo\" onerror=\"this.style.display='none'\">
              <h1>${companyInfo.name || 'JANNAAT PATTAYA CLUB'}</h1>
              <div class=\"company-info\">
                <div>${companyInfo.address || ''}</div>
                <div>Tel: ${companyInfo.phone_number || ''}</div>
                <div>Tax ID: ${companyInfo.tax_id || ''}</div>
              </div>
            </div>
            
            <!-- Voucher Title -->
            <div class=\"voucher-title\">ใบสำคัญจ่าย / PAYMENT VOUCHER</div>
            
            <!-- Voucher Details -->
            <div class=\"voucher-details\">
              <div class=\"detail-row\">
                <div class=\"detail-label\">เลขที่ใบสำคัญ (Voucher No.):</div>
                <div class=\"detail-value\">${record.pno || record.id}</div>
              </div>
              
              <div class=\"detail-row\">
                <div class=\"detail-label\">วันที่ (Date):</div>
                <div class=\"detail-value\">${voucherDate}</div>
              </div>
              
              <div class=\"detail-row\">
                <div class=\"detail-label\">วันที่ใบแจ้งหนี้ (Invoice Date):</div>
                <div class=\"detail-value\">${invoiceDate}</div>
              </div>
              
              <div class=\"detail-row\">
                <div class=\"detail-label\">จ่ายให้ (Pay To):</div>
                <div class=\"detail-value\">${record.subhead || '-'}</div>
              </div>
              
              <div class=\"detail-row\">
                <div class=\"detail-label\">เลขที่อ้างอิง (Reference No.):</div>
                <div class=\"detail-value\">${record.ref || '-'}</div>
              </div>
              
              <div class=\"detail-row\">
                <div class=\"detail-label\">หมวดหมู่ (Category):</div>
                <div class=\"detail-value\">${record.head || '-'}</div>
              </div>
              
              <div class=\"detail-row\">
                <div class=\"detail-label\">วิธีชำระเงิน (Payment Method):</div>
                <div class=\"detail-value\">${record.payment_mode || '-'}</div>
              </div>
            </div>
            
            <!-- Amount Details Table -->
            <table>
              <thead>
                <tr>
                  <th style=\"width: 60%;\">รายการ (Description)</th>
                  <th class=\"text-right\" style=\"width: 20%;\">จำนวนเงิน (Amount)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${record.remark || 'Payment'}</td>
                  <td class=\"text-right\">฿${parseFloat(record.credit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                ${record.vat && parseFloat(record.vat) > 0 ? `
                <tr>
                  <td>ภาษีมูลค่าเพิ่ม 7% (VAT 7%)</td>
                  <td class=\"text-right\">฿${parseFloat(record.vat).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                ` : ''}
                ${record.Disc && parseFloat(record.Disc) > 0 ? `
                <tr>
                  <td>ส่วนลด (Discount)</td>
                  <td class=\"text-right\">-฿${parseFloat(record.Disc).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                ` : ''}
              </tbody>
            </table>
            
            <!-- Amount Section -->
            <div class=\"amount-section\">
              <div class=\"amount-row total\">
                <span>ยอดเงินที่จ่าย (Total Paid):</span>
                <span>฿${parseFloat(record.debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              
              <div class=\"amount-in-words\">
                <strong>จำนวนเงิน (Amount in Words):</strong> ${amountInWords}
              </div>
              
              ${record.balance && parseFloat(record.balance) !== 0 ? `
              <div class=\"amount-row\" style=\"margin-top: 10px;\">
                <span>คงเหลือ (Balance):</span>
                <span>฿${parseFloat(record.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              ` : ''}
            </div>
            
            <!-- Signature Section -->
            <div class=\"signature-section\">
              <div class=\"signature-box\">
                <div class=\"signature-line\">
                  ผู้จ่ายเงิน (Paid By)
                </div>
                <div style=\"margin-top: 5px;\">วันที่ (Date): _______________</div>
              </div>
              
              <div class=\"signature-box\">
                <div class=\"signature-line\">
                  ผู้รับเงิน (Received By)
                </div>
                <div style=\"margin-top: 5px;\">วันที่ (Date): _______________</div>
              </div>
            </div>
            
            <!-- Footer -->
            <div class=\"footer\">
              <p>เอกสารนี้ออกโดยระบบคอมพิวเตอร์ / This document is computer generated</p>
              <p>พิมพ์เมื่อ (Printed on): ${new Date().toLocaleString('th-TH')}</p>
              ${companyInfo.developer ? `<p>Powered by ${companyInfo.developer}</p>` : ''}
            </div>
          </div>
        </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
      };
      
    } catch (error) {
      console.error('Error printing payment voucher:', error);
      if (toast && typeof toast.error === 'function') {
        toast.error('Failed to print payment voucher.');
      }
    }
  }, []);

  // Handle edit form submission - Optimized
  const handleEditSubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      await axios.put(`/updatedata/expenses/${editingRecord.id}`, formData, getHeaders());
      if (toast && typeof toast.success === 'function') {
        toast.success("Expense record updated successfully!");
      }

      // Close modal and refresh data
      setShowEditModal(false);
      setEditingRecord(null);
      loadExpensesData();
      
    } catch (error) {
      console.error(error);
      if (toast && typeof toast.error === 'function') {
        toast.error("Failed to update expense record.");
      }
    }
  }, [editingRecord, formData, loadExpensesData]);

  // Close edit modal - Optimized
  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditingRecord(null);
  }, []);

  // Export Functions
  const exportToPDF = useCallback(() => {
    const doc = new jsPDF('landscape'); // Set to landscape orientation
    
    // Add logo at top right corner
    const logoUrl = '/logo.png'; // Path to your logo in public folder
    const imgWidth = 40;
    const imgHeight = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const xPosition = pageWidth - imgWidth - 14; // 14 is margin from right
    
    try {
      // Add logo image if it exists
      const img = new Image();
      img.src = logoUrl;
      doc.addImage(img, 'PNG', xPosition, 10, imgWidth, imgHeight);
    } catch (error) {
      console.log('Logo not found, skipping...');
    }
    
    // Add title
    doc.setFontSize(16);
    doc.text('Expense Report', 14, 22);
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);
    
    // Prepare table data
    const tableColumn = ["Date", "Head", "Subhead", "Ref", "Payment Mode", "Bank", "Credit", "VAT", "Discount", "Debit", "Balance", "Remark"];
    const tableRows = [];
    
    const dataToExport = filteredData.length > 0 ? filteredData : data;
    let closingBalance = 0;
    
    dataToExport.forEach(record => {
      const balance = parseFloat(record.balance) || 0;
      if (balance > closingBalance) {
        closingBalance = balance;
      }
      
      const rowData = [
        record.date || '',
        record.head || '',
        record.subhead || '',
        record.ref || '',
        record.payment_mode || '',
        record.bank || '',
        record.credit || '',
        record.vat || '',
        record.Disc || '',
        record.debit || '',
        record.balance || '',
        record.remark || ''
      ];
      tableRows.push(rowData);
    });
    
    // Add closing balance row
    tableRows.push([
      '', '', '', '', '', '', '', '', '', '',
      `฿${closingBalance.toLocaleString()}`,
      'Closing Balance'
    ]);
    
    // Add table
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      styles: { fontSize: 10 }, // Increased font size for landscape
      headStyles: { fillColor: [44, 123, 229] },
      didParseCell: function (data) {
        // Style the closing balance row
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    
    // Save the PDF
    doc.save(`expense-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exported successfully!');
  }, [data, filteredData]);

  const exportToExcel = useCallback(() => {
    // Prepare data for Excel
    const dataToExport = filteredData.length > 0 ? filteredData : data;
    let closingBalance = 0;
    
    const excelData = dataToExport.map(record => {
      const balance = parseFloat(record.balance) || 0;
      if (balance > closingBalance) {
        closingBalance = balance;
      }
      
      return {
        'Date': record.date || '',
        'Head': record.head || '',
        'Subhead': record.subhead || '',
        'Ref': record.ref || '',
        'Payment Mode': record.payment_mode || '',
        'Bank': record.bank || '',
        'Credit': record.credit || '',
        'VAT': record.vat || '',
        'Discount': record.Disc || '',
        'Debit': record.debit || '',
        'Balance': record.balance || '',
        'Remark': record.remark || ''
      };
    });
    
    // Add closing balance row
    excelData.push({
      'Date': '',
      'Head': '',
      'Subhead': '',
      'Ref': '',
      'Payment Mode': '',
      'Bank': '',
      'Credit': '',
      'VAT': '',
      'Discount': '',
      'Debit': '',
      'Balance': `฿${closingBalance.toLocaleString()}`,
      'Remark': 'Closing Balance'
    });
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      {wch: 12}, {wch: 15}, {wch: 15}, {wch: 12}, {wch: 12}, 
      {wch: 10}, {wch: 10}, {wch: 8}, {wch: 10}, {wch: 10}, 
      {wch: 10}, {wch: 20}
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    
    // Save the file
    XLSX.writeFile(wb, `expense-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel file exported successfully!');
  }, [data, filteredData]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Expense Report - ${format(new Date(), 'yyyy-MM-dd')}`,
    onAfterPrint: () => {
      toast.success('Print document prepared successfully!');
    }
  });

  // Fetch Summary by Subhead from Backend
  const fetchSubheadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/accounts/balance-by-subhead`,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        // Filter only positive balances and format data
        const filteredData = response.data.data
          .filter(item => parseFloat(item.balance) > 0)
          .map(item => ({
            subhead: item.subhead,
            balance: parseFloat(item.balance),
            total_credit: parseFloat(item.total_credit),
            total_debit: parseFloat(item.total_debit)
          }));
        
        setSummaryData(filteredData);
        return filteredData;
      }
    } catch (error) {
      console.error('Error fetching subhead summary:', error);
      toast.error('Failed to fetch summary data');
      setSummaryData([]);
      return [];
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const toggleSummaryTable = useCallback(async () => {
    const newState = !showSummaryTable;
    setShowSummaryTable(newState);
    
    // Fetch data when opening summary
    if (newState) {
      await fetchSubheadSummary();
    }
  }, [showSummaryTable, fetchSubheadSummary]);

  const resetSummaryView = useCallback(() => {
    setShowSummaryTable(false);
    setSummaryData([]);
  }, []);

  // Export Summary to PDF
  const exportSummaryToPDF = useCallback(() => {
    if (summaryData.length === 0) {
      toast.warning('No pending balances to export!');
      return;
    }
    
    const sortedSubheads = summaryData
      .sort((a, b) => b.balance - a.balance)
      .map(item => [item.subhead, item.balance]);
    const totalPending = summaryData.reduce((sum, item) => sum + item.balance, 0);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add professional header background with light blue gradient color
    doc.setFillColor(79, 172, 254); // Light blue color
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    // Company/Report Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('PENDING BALANCE SUMMARY', pageWidth / 2, 10, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('By Subhead Category', pageWidth / 2, 16, { align: 'center' });
    
    // Date and time
    doc.setFontSize(8);
    doc.text(`Report Generated: ${format(new Date(), 'dd MMMM yyyy | HH:mm:ss')}`, pageWidth / 2, 21, { align: 'center' });
    
    // Prepare table data with better formatting
    const tableData = sortedSubheads.map(([subhead, amount], index) => {
      const percentage = ((amount / totalPending) * 100).toFixed(1);
      return [
        index + 1,
        subhead,
        amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        percentage + '%'
      ];
    });
    
    // Add table with professional styling
    doc.autoTable({
      startY: 32,
      head: [['No.', 'Subhead Category', 'Amount (THB)', 'Share (%)']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 172, 254], // Light blue color
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        cellPadding: 5
      },
      bodyStyles: {
        fontSize: 10,
        font: 'helvetica',
        textColor: [50, 50, 50],
        cellPadding: 4
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15, fontStyle: 'bold' },
        1: { halign: 'left', cellWidth: 85, fontStyle: 'normal' },
        2: { halign: 'right', cellWidth: 45, fontStyle: 'bold', textColor: [214, 48, 49] },
        3: { halign: 'center', cellWidth: 30, fontStyle: 'normal' }
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      styles: {
        lineColor: [220, 220, 220],
        lineWidth: 0.1
      }
    });
    
    // Add total section with professional styling
    const finalY = doc.lastAutoTable.finalY + 8;
    
    // Draw elegant border for total box
    doc.setDrawColor(79, 172, 254); // Light blue border
    doc.setLineWidth(0.5);
    doc.roundedRect(14, finalY, pageWidth - 28, 16, 2, 2);
    
    // Background gradient effect (simulated with two rectangles)
    doc.setFillColor(255, 249, 235);
    doc.roundedRect(14, finalY, pageWidth - 28, 16, 2, 2, 'F');
    
    // Total label
    doc.setFontSize(13);
    doc.setTextColor(50, 50, 50);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL PENDING BALANCE:', 20, finalY + 10);
    
    // Total amount
    doc.setFontSize(14);
    doc.setTextColor(214, 48, 49);
    doc.setFont('helvetica', 'bold');
    const totalText = totalPending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' THB';
    doc.text(totalText, pageWidth - 20, finalY + 10, { align: 'right' });
    
    // Summary statistics box
    const statsY = finalY + 25;
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Categories: ${sortedSubheads.length}`, 20, statsY);
    doc.text(`Highest: ${sortedSubheads[0][0]} (${sortedSubheads[0][1].toLocaleString('en-US', { minimumFractionDigits: 2 })} THB)`, 20, statsY + 5);
    
    // Footer with divider line
    const footerY = doc.internal.pageSize.getHeight() - 15;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(20, footerY - 3, pageWidth - 20, footerY - 3);
    
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'italic');
    doc.text('Jannaat Expense Management System', 20, footerY);
    doc.text(`Page 1 of 1`, pageWidth - 20, footerY, { align: 'right' });
    doc.text('Confidential Document', pageWidth / 2, footerY, { align: 'center' });
    
    // Save the PDF
    doc.save(`Pending_Balance_Summary_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
    toast.success('Summary exported to PDF successfully!');
  }, [summaryData]);

  // Initialize charts when summary table is shown
  useEffect(() => {
    if (showSummaryTable && summaryData.length > 0) {
      const sortedData = [...summaryData].sort((a, b) => b.balance - a.balance);
      
      const labels = sortedData.map(item => item.subhead);
      const dataValues = sortedData.map(item => item.balance);
      const colors = [
        '#4facfe', '#00d4ff', '#6dd5ed', '#2193b0',
          '#43e97b', '#fa709a', '#fee140', '#30cfd0',
        '#a8edea', '#fed6e3', '#89f7fe', '#66a6ff'
      ];
      
      // Destroy existing charts if they exist
      const barCanvas = document.getElementById('summaryBarChart');
      const pieCanvas = document.getElementById('summaryPieChart');
      
      if (barCanvas && window.barChartInstance) {
        window.barChartInstance.destroy();
      }
      if (pieCanvas && window.pieChartInstance) {
        window.pieChartInstance.destroy();
      }
      
      // Create Bar Chart
      if (barCanvas) {
        const barCtx = barCanvas.getContext('2d');
        window.barChartInstance = new Chart(barCtx, {
            type: 'bar',
            data: {
              labels: labels,
              datasets: [{
                label: 'Pending Amount (฿)',
                data: dataValues,
                backgroundColor: colors.slice(0, labels.length),
                borderColor: colors.slice(0, labels.length).map(c => c + 'cc'),
                borderWidth: 2,
                borderRadius: 8
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      return '฿' + context.parsed.y.toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      });
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return '฿' + value.toLocaleString();
                    }
                  }
                },
                x: {
                  ticks: {
                    maxRotation: 45,
                    minRotation: 45
                  }
                }
              }
            }
          });
      }
      
      // Create Pie Chart
      if (pieCanvas) {
        const pieCtx = pieCanvas.getContext('2d');
          window.pieChartInstance = new Chart(pieCtx, {
            type: 'pie',
            data: {
              labels: labels,
              datasets: [{
                data: dataValues,
                backgroundColor: colors.slice(0, labels.length),
                borderColor: '#fff',
                borderWidth: 2
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'right',
                  labels: {
                    boxWidth: 15,
                    padding: 10,
                    font: {
                      size: 11
                    }
                  }
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const label = context.label || '';
                      const value = context.parsed || 0;
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = ((value / total) * 100).toFixed(1);
                      return label + ': ฿' + value.toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      }) + ' (' + percentage + '%)';
                    }
                  }
                }
              }
            }
          });
      }
    }
    
    // Cleanup function
    return () => {
      if (window.barChartInstance) {
        window.barChartInstance.destroy();
        window.barChartInstance = null;
      }
      if (window.pieChartInstance) {
        window.pieChartInstance.destroy();
        window.pieChartInstance = null;
      }
    };
  }, [showSummaryTable, summaryData]);

  // Filter Functions
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...data];
    
    console.log('Applying filters:', filters);
    console.log('Total data records:', data.length);

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(item => {
        const itemDate = item.date || item.dte; // Support both field names
        return itemDate && new Date(itemDate) >= new Date(filters.dateFrom);
      });
      console.log('After dateFrom filter:', filtered.length);
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(item => {
        const itemDate = item.date || item.dte;
        return itemDate && new Date(itemDate) <= new Date(filters.dateTo);
      });
      console.log('After dateTo filter:', filtered.length);
    }

    // Head filter
    if (filters.head) {
      filtered = filtered.filter(item => 
        item.head && item.head === filters.head
      );
      console.log('After head filter:', filtered.length);
    }

    // Subhead filter
    if (filters.subhead) {
      filtered = filtered.filter(item => 
        item.subhead && item.subhead === filters.subhead
      );
      console.log('After subhead filter:', filtered.length);
    }

    // Payment mode filter
    if (filters.paymentMode) {
      filtered = filtered.filter(item => {
        const paymentMode = item.payment_mode || item.pmode; // Support both field names
        return paymentMode && paymentMode.toLowerCase().includes(filters.paymentMode.toLowerCase());
      });
      console.log('After payment mode filter:', filtered.length);
    }

    // Amount filter
    if (filters.amountValue) {
      const filterAmount = parseFloat(filters.amountValue);
      if (!isNaN(filterAmount)) {
        filtered = filtered.filter(item => {
          const debitAmount = parseFloat(item.debit || 0);
          const creditAmount = parseFloat(item.credit || 0);
          const totalAmount = Math.max(debitAmount, creditAmount);
          return filters.amountOperator === "greater" 
            ? totalAmount > filterAmount 
            : totalAmount < filterAmount;
        });
        console.log('After amount filter:', filtered.length);
      }
    }

    console.log('Final filtered data:', filtered.length);
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [data, filters]);

  const clearFilters = useCallback(() => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      head: "",
      subhead: "",
      paymentMode: "",
      amountOperator: "greater",
      amountValue: ""
    });
    setFilteredData(data);
    setCurrentPage(1);
  }, [data]);

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // Get unique heads and subheads for dropdown options
  const uniqueHeads = useMemo(() => {
    const heads = data.map(item => item.head).filter(head => head && head.trim() !== '');
    return [...new Set(heads)].sort();
  }, [data]);

  const uniqueSubheads = useMemo(() => {
    const subheads = data.map(item => item.subhead).filter(subhead => subhead && subhead.trim() !== '');
    return [...new Set(subheads)].sort();
  }, [data]);

  // Apply filters when data or filters change
  useEffect(() => {
    if (data.length > 0) {
      applyFilters();
    }
  }, [data, filters, applyFilters]);

  // Initialize filtered data when data loads
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  return (
    <Layout>
      {/* Header with Topbar */}
      <Header title="Expense Management" />
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Filter Form */}
      <div className="card mb-4" style={{ width: '80%' }}>
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-filter mr-2"></i>
              Data Filters
              {filteredData.length !== data.length && (
                <span className="badge badge-primary ml-2">
                  {filteredData.length} of {data.length} records
                </span>
              )}
            </h6>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={toggleFilters}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              <i className={`fas fa-chevron-${showFilters ? 'up' : 'down'} ml-1`}></i>
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className="card-body">
            <form className="row g-3">
              {/* Date Range Filters */}
              <div className="col-lg-3 col-md-6">
                <label className="form-label">Date From</label>
                <input
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm"
                />
              </div>
              
              <div className="col-lg-3 col-md-6">
                <label className="form-label">Date To</label>
                <input
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm"
                />
              </div>
              
              {/* Head Filter */}
              <div className="col-lg-3 col-md-6">
                <label className="form-label">Head</label>
                <select
                  name="head"
                  value={filters.head}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm"
                >
                  <option value="">All Heads</option>
                  {uniqueHeads.map((head, index) => (
                    <option key={index} value={head}>
                      {head}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Subhead Filter */}
              <div className="col-lg-3 col-md-6">
                <label className="form-label">Subhead</label>
                <select
                  name="subhead"
                  value={filters.subhead}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm"
                >
                  <option value="">All Subheads</option>
                  {uniqueSubheads.map((subhead, index) => (
                    <option key={index} value={subhead}>
                      {subhead}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Payment Mode Filter */}
              <div className="col-lg-3 col-md-6">
                <label className="form-label">Payment Mode</label>
                <select
                  name="paymentMode"
                  value={filters.paymentMode}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm"
                >
                  <option value="">All Payment Modes</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                </select>
              </div>
              
              {/* Amount Filter */}
              <div className="col-lg-2 col-md-3">
                <label className="form-label">Amount</label>
                <select
                  name="amountOperator"
                  value={filters.amountOperator}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm"
                >
                  <option value="greater">Greater than</option>
                  <option value="less">Less than</option>
                </select>
              </div>
              
              <div className="col-lg-2 col-md-3">
                <label className="form-label">&nbsp;</label>
                <input
                  type="number"
                  name="amountValue"
                  value={filters.amountValue}
                  onChange={handleFilterChange}
                  placeholder="Amount..."
                  className="form-control form-control-sm"
                  min="0"
                  step="0.01"
                />
              </div>
              
              {/* Filter Actions */}
              <div className="col-lg-2 col-md-6">
                <label className="form-label">&nbsp;</label>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="btn btn-primary btn-sm"
                    title="Apply Filters"
                  >
                    <i className="fas fa-filter"></i>
                  </button>
                  
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="btn btn-outline-secondary btn-sm"
                    title="Clear Filters"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </form>
            
            {/* Filter Summary */}
            {(filters.dateFrom || filters.dateTo || filters.head || filters.subhead || filters.paymentMode || filters.amountValue) && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-muted">
                  <strong>Active Filters:</strong>
                  {filters.dateFrom && <span className="badge badge-info mr-1">From: {filters.dateFrom}</span>}
                  {filters.dateTo && <span className="badge badge-info mr-1">To: {filters.dateTo}</span>}
                  {filters.head && <span className="badge badge-info mr-1">Head: {filters.head}</span>}
                  {filters.subhead && <span className="badge badge-info mr-1">Subhead: {filters.subhead}</span>}
                  {filters.paymentMode && <span className="badge badge-info mr-1">Mode: {filters.paymentMode}</span>}
                  {filters.amountValue && (
                    <span className="badge badge-info mr-1">
                      Amount {filters.amountOperator === 'greater' ? '>' : '<'} {filters.amountValue}
                    </span>
                  )}
                </small>
              </div>
            )}
          </div>
        )}
        
      </div>

      {/* Topbar with Add Button */}
      <div className="row mb-3" style={{ marginLeft: '10px' }}>
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            
            <div className="d-flex gap-2">
              <button 
                className="btn btn-primary btn-sm"
                onClick={exportToPDF}
                title="Export to PDF"
              >
                <i className="fas fa-file-pdf mr-1"></i>
                PDF
              </button>
              
              <button 
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                title="Export to Excel"
              >
                <i className="fas fa-file-excel mr-1"></i>
                Excel
              </button>
              
              <button 
                className="btn btn-info btn-sm"
                onClick={handlePrint}
                title="Print Table"
              >
                <i className="fas fa-print mr-1"></i>
                Print
              </button>
              
              <button 
                className="btn btn-sm"
                onClick={toggleSummaryTable}
                title="Toggle Summary by Subhead"
                style={{
                  backgroundColor: showSummaryTable ? '#ff9800' : '#fff3e0',
                  color: showSummaryTable ? 'white' : '#e65100',
                  border: '1px solid #ffb74d',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = showSummaryTable ? '#f57c00' : '#ffe0b2';
                  e.currentTarget.style.borderColor = '#ff9800';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = showSummaryTable ? '#ff9800' : '#fff3e0';
                  e.currentTarget.style.borderColor = '#ffb74d';
                }}
              >
                <i className="fas fa-chart-pie mr-1"></i>
                Summary
              </button>
            </div>
            
          
          </div>
        </div>
      </div>

      {/* Expenses Data Table */}
      {!showSummaryTable && (
        <div ref={printRef}>
          <div className="table-responsive">
            {data.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No expense records available.</p>
                <button 
                  className="btn btn-primary"
                  onClick={openNewExpenseModal}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add First Expense
                </button>
              </div>
            ) : (
            <div>
              <table className="table table-striped table-bordered table-hover">
                <thead className="thead-light">
                  <tr>
                    {columns.map((col, index) => (
                      <th key={index} className={`text-${col.align}`}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginationData.currentItems.map((record, index) => (
                    <tr key={record.id || index}>
                      <td>{record.date}</td>
                      <td>{record.head}</td>
                      <td>{record.subhead}</td>
                      <td>{record.ref}</td>
                      <td>{record.payment_mode}</td>
                      <td className="text-right font-weight-bold">
                        {record.credit ? `฿${parseFloat(record.credit).toLocaleString()}` : '-'}
                      </td>
                      <td className="text-right font-weight-bold">
                        {record.Disc ? `฿${parseFloat(record.Disc).toLocaleString()}` : '-'}
                      </td>
                      <td className="text-right font-weight-bold">
                        {record.debit ? `฿${parseFloat(record.debit).toLocaleString()}` : '-'}
                      </td>
                      <td className="text-right font-weight-bold">
                        {record.balance ? `฿${parseFloat(record.balance).toLocaleString()}` : '-'}
                      </td>
                      <td>{record.remark}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center align-items-center" style={{ gap: '8px' }}>
                          {/* Show Print icon only if debit amount exists */}
                          {record.debit && parseFloat(record.debit) > 0 && (
                            <button
                              className="btn btn-sm"
                              onClick={() => printPaymentVoucher(record)}
                              title="Print Payment Voucher"
                              style={{ 
                                minWidth: '32px', 
                                height: '32px', 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                backgroundColor: '#e0f7fa',
                                color: '#00838f',
                                border: '1px solid #00acc1',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#b2ebf2';
                                e.currentTarget.style.borderColor = '#00838f';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#e0f7fa';
                                e.currentTarget.style.borderColor = '#00acc1';
                              }}
                            >
                              <i className="fas fa-print"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditRecord(record)}
                            title="Edit Record"
                            style={{ minWidth: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteRecord(record.id)}
                            title="Delete Record"
                            style={{ minWidth: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Items per page and Pagination Controls */}
              {data.length > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                    <div className="d-flex align-items-center">
                      <small className="text-muted mr-2">Show</small>
                      <select 
                        className="form-control form-control-sm" 
                        style={{ width: 'auto', display: 'inline-block' }}
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <small className="text-muted ml-2">entries per page</small>
                    </div>
                    
                    <div className="d-flex align-items-center">
                      <small className="text-muted">
                        Showing {paginationData.startItem} to {paginationData.endItem} of {paginationData.totalItems} entries
                      </small>
                    </div>
                  </div>
                </>
              )}
              
              {/* Closing Balance Display */}
              {data.length > 0 && (
                <div className="d-flex justify-content-end align-items-center mt-3 mb-3">
                  <div className="card border-success" style={{ minWidth: '250px' }}>
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-success font-weight-bold">
                          <i className="fas fa-wallet mr-2"></i>
                          Closing Balance:
                        </span>
                        <span className="h5 text-success font-weight-bold mb-0">
                          ฿{closingBalance.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Pagination Controls */}
              {paginationData.totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center">
                  <nav aria-label="Expenses pagination">
                    <ul className="pagination pagination-sm mb-0">
                      {/* First Page Button */}
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={goToFirstPage}
                          disabled={currentPage === 1}
                          title="First Page"
                        >
                          <i className="fas fa-angle-double-left"></i>
                        </button>
                      </li>
                      
                      {/* Previous Page Button */}
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={goToPrevPage}
                          disabled={currentPage === 1}
                          title="Previous Page"
                        >
                          <i className="fas fa-angle-left"></i>
                        </button>
                      </li>
                      
                      {/* Page Numbers */}
                      {(() => {
                        const totalPages = paginationData.totalPages;
                        const current = currentPage;
                        const maxButtons = 5; // Minimum 5 buttons visible
                        const range = [];
                        const rangeWithDots = [];
                        
                        if (totalPages <= maxButtons) {
                          // Show all pages if total is 5 or less
                          for (let i = 1; i <= totalPages; i++) {
                            range.push(i);
                          }
                        } else {
                          // Always show first page
                          range.push(1);
                          
                          // Calculate the range of page numbers to show
                          let startPage = Math.max(2, current - 1);
                          let endPage = Math.min(totalPages - 1, current + 1);
                          
                          // Adjust to ensure at least 5 buttons
                          const currentRange = endPage - startPage + 3; // +3 for first, last, and current ranges
                          if (currentRange < maxButtons) {
                            if (current <= 3) {
                              endPage = Math.min(totalPages - 1, maxButtons - 1);
                            } else if (current >= totalPages - 2) {
                              startPage = Math.max(2, totalPages - maxButtons + 2);
                            }
                          }
                          
                          // Add pages in the middle range
                          for (let i = startPage; i <= endPage; i++) {
                            range.push(i);
                          }
                          
                          // Always show last page
                          range.push(totalPages);
                        }
                        
                        // Remove duplicates and sort
                        const uniqueRange = [...new Set(range)].sort((a, b) => a - b);
                        
                        // Add dots between non-consecutive pages
                        for (let i = 0; i < uniqueRange.length; i++) {
                          if (i === 0) {
                            rangeWithDots.push(uniqueRange[i]);
                          } else if (uniqueRange[i] - uniqueRange[i - 1] === 1) {
                            rangeWithDots.push(uniqueRange[i]);
                          } else {
                            rangeWithDots.push('...', uniqueRange[i]);
                          }
                        }
                        
                        return rangeWithDots.map((page, index) => {
                          if (page === '...') {
                            return (
                              <li key={`dots-${index}`} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          
                          return (
                            <li key={page} className={`page-item ${current === page ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => goToPage(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        });
                      })()}
                      
                      {/* Next Page Button */}
                      <li className={`page-item ${currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={goToNextPage}
                          disabled={currentPage === paginationData.totalPages}
                          title="Next Page"
                        >
                          <i className="fas fa-angle-right"></i>
                        </button>
                      </li>
                      
                      {/* Last Page Button */}
                      <li className={`page-item ${currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={goToLastPage}
                          disabled={currentPage === paginationData.totalPages}
                          title="Last Page"
                        >
                          <i className="fas fa-angle-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>
        {/* End of print wrapper */}
      </div>
      )}
        
        <style jsx>{`
          .pagination .page-link {
            color: #007bff;
            border-color: #dee2e6;
          }
          .pagination .page-item.active .page-link {
            background-color: #007bff;
            border-color: #007bff;
          }
          .pagination .page-item.disabled .page-link {
            color: #6c757d;
            background-color: #fff;
            border-color: #dee2e6;
          }
          .pagination .page-link:hover {
            color: #0056b3;
            background-color: #e9ecef;
            border-color: #dee2e6;
          }
          .pagination .page-item.active .page-link:hover {
            background-color: #0056b3;
            border-color: #0056b3;
          }
          
          /* Export Buttons Styling */
          .d-flex.gap-2 {
            gap: 0.5rem;
          }
          
          .btn-sm {
            font-size: 0.875rem;
            padding: 0.375rem 0.75rem;
            border-radius: 0.25rem;
          }
          
          .btn-sm i {
            font-size: 0.875rem;
          }
          
          @media (max-width: 768px) {
            .d-flex.gap-2 {
              flex-direction: column;
              gap: 0.25rem;
            }
            
            .btn-sm {
              font-size: 0.8rem;
              padding: 0.25rem 0.5rem;
            }
          }
          
          /* Filter Form Styles */
          .card {
            width: 98% !important;
            max-width: none !important;
          }
          
          .card-header {
            background-color: #f8f9fa !important;
            border-bottom: 1px solid #dee2e6;
          }
          
          .badge {
            font-size: 0.75em;
            margin-right: 0.25rem;
            margin-bottom: 0.25rem;
          }
          
          .badge-info {
            background-color: #17a2b8;
            color: white;
          }
          
          .badge-primary {
            background-color: #007bff;
            color: white;
          }
          
          .form-label {
            font-weight: 500;
            color: #495057;
            margin-bottom: 0.25rem;
          }
          
          .form-control-sm:focus {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          
          select.form-control-sm {
            height: calc(1.5em + 0.5rem + 2px);
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
            background-color: #fff;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right 0.75rem center;
            background-size: 16px 12px;
          }
          
          select.form-control-sm:focus {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          
          .btn-outline-secondary {
            border-color: #6c757d;
          }
          
          .btn-outline-secondary:hover {
            background-color: #6c757d;
            border-color: #6c757d;
          }
          
          /* Closing Balance Styles */
          .card.border-success {
            border-color: #28a745 !important;
            border-width: 2px;
            box-shadow: 0 2px 4px rgba(40, 167, 69, 0.1);
            background: linear-gradient(135deg, #f8fff9 0%, #f1f8f2 100%);
          }
          
          .card.border-success .card-body {
            background: transparent;
          }
          
          .text-success {
            color: #28a745 !important;
          }
          
          .fas.fa-wallet {
            font-size: 1.1em;
          }
          
          @media (max-width: 768px) {
            .card.border-success {
              min-width: 200px !important;
            }
            
            .card.border-success .card-body {
              padding: 0.5rem !important;
            }
            
            .card.border-success .h5 {
              font-size: 1.1rem !important;
            }
          }
        `}</style>
     

      {/* Modal for Adding New Expense */}
      {showModal && (
        <div 
          className="modal fade show expense-modal-backdrop" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="modal-dialog modal-lg" style={{ maxWidth: '700px', width: '100%', margin: 'auto' }}>
            <div className="modal-content expense-modal-content">
              <div className="modal-header bg-success text-white sticky-top">
                <h4 className="modal-title">
                  <i className="fas fa-plus mr-2"></i>
                  Add New Expense Entry
                </h4>
                <button 
                  type="button" 
                  className="close text-white" 
                  onClick={closeModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              
              <div className="modal-body expense-modal-body">
                <form onSubmit={handleSubmit} className="form-horizontal">
                  <div className="row g-3">
                    {/* Head */}
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="control-label">Head <span className="text-danger">*</span></label>
                        <select
                          name="head"
                          value={formData.head}
                          onChange={handleHeadChange}
                          className="form-control form-control-sm"
                          required
                        >
                          <option value="">Select Head</option>
                          {heads.map((head) => (
                            <option key={head.id} value={head.name}>
                              {head.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Sub Head */}
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="control-label">Sub Head</label>
                        <select
                          name="subhead"
                          value={formData.subhead}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        >
                          <option value="">Select Sub Head</option>
                          {subheads.map((subhead) => (
                            <option key={subhead.id} value={subhead.subhead}>
                              {subhead.subhead}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Invoice/Ref No */}
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="control-label">Invoice/Ref No.</label>
                        <input
                          type="text"
                          name="ref"
                          value={formData.ref}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          placeholder="Enter Invoice/Ref No..."
                        />
                      </div>
                    </div>

                    {/* Payment Mode */}
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="control-label">Payment Mode</label>
                        <select
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        >
                          <option value="cash">Cash</option>
                          <option value="credit">Credit</option>
                          <option value="cheque">Cheque</option>
                          <option value="btransfer">Bank Transfer</option>
                        </select>
                      </div>
                    </div>

                    {/* Previous Balance */}
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="control-label">
                          Previous Balance
                          {isLoadingBalance && <i className="fas fa-spinner fa-spin ml-2"></i>}
                        </label>
                        <input
                          type="number"
                          name="prebalance"
                          value={formData.prebalance}
                          readOnly
                          className="form-control form-control-sm bg-light"
                          placeholder={isLoadingBalance ? "Loading..." : "0"}
                        />
                      </div>
                    </div>

                    {/* Credit */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Credit</label>
                        <input
                          type="number"
                          name="credit"
                          value={formData.credit}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        />
                      </div>
                    </div>

                    {/* Include VAT Checkbox */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Include VAT</label>
                        <div className="checkbox mt-2">
                          <input
                            type="checkbox"
                            name="includeVat"
                            checked={formData.includeVat}
                            onChange={handleInputChange}
                          />
                          <label className="ml-2">Auto-calculate 7% VAT</label>
                        </div>
                      </div>
                    </div>

                    {/* VAT Amount */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">VAT Amount (7%)</label>
                        <input
                          type="number"
                          name="vat"
                          value={formData.vat}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Discount */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Discount</label>
                        <input
                          type="number"
                          name="Disc"
                          value={formData.Disc}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Debit - Payment Paid */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Debit - Payment Paid</label>
                        <input
                          type="number"
                          name="debit"
                          value={formData.debit}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Balance */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Balance</label>
                        <input
                          type="number"
                          name="balance"
                          value={formData.balance}
                          readOnly
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Remark */}
                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Remark</label>
                        <input
                          type="text"
                          name="remark"
                          value={formData.remark}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          placeholder="Enter remarks..."
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              
              <div className="modal-footer sticky-bottom bg-light">
                <button 
                  type="button" 
                  className="btn btn-secondary btn-sm" 
                  onClick={closeModal}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-success btn-sm"
                  onClick={handleSubmit}
                >
                  <i className="fas fa-save mr-2"></i>
                  Save Expense
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div 
          className="modal fade show expense-modal-backdrop" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
            padding: '20px'
          }}
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              closeEditModal();
            }
          }}
        >
          <div className="modal-dialog modal-lg" style={{ maxWidth: '850px', width: '100%', margin: 0 }}>
            <div className="modal-content expense-modal-content">
              <div className="modal-header bg-primary text-white sticky-top">
                <h4 className="modal-title">
                  <i className="fas fa-edit mr-2"></i>
                  Edit Expense Record #{editingRecord?.id}
                </h4>
                <button 
                  type="button" 
                  className="close text-white" 
                  onClick={closeEditModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              
              <div className="modal-body expense-modal-body">
                <form onSubmit={handleEditSubmit} className="form-horizontal">
                  <div className="row g-3">
                    {/* Date (readonly) */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Date</label>
                        <input
                          type="text"
                          name="date"
                          value={formData.date}
                          readOnly
                          className="form-control form-control-sm"
                          placeholder="Enter Date..."
                        />
                      </div>
                    </div>

                    {/* Invoice Date */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Invoice Date</label>
                        <input
                          type="date"
                          name="invdte"
                          value={formData.invdte}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        />
                      </div>
                    </div>

                    {/* User ID */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">User ID</label>
                        <input
                          type="text"
                          name="userid"
                          value={formData.userid}
                          readOnly
                          className="form-control form-control-sm"
                          placeholder="Enter User ID..."
                        />
                      </div>
                    </div>

                    {/* Head */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Head</label>
                        <select
                          name="head"
                          value={formData.head}
                          onChange={handleHeadChange}
                          className="form-control form-control-sm"
                          required
                        >
                          <option value="">Select Head</option>
                          {heads.map((head) => (
                            <option key={head.id} value={head.name}>
                              {head.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Sub Head */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Sub Head</label>
                        <select
                          name="subhead"
                          value={formData.subhead}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        >
                          <option value="">Select Sub Head</option>
                          {subheads.map((subhead) => (
                            <option key={subhead.id} value={subhead.subhead}>
                              {subhead.subhead}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Invoice/Ref No */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Invoice/Ref No.</label>
                        <input
                          type="text"
                          name="ref"
                          value={formData.ref}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          placeholder="Enter Invoice/Ref No..."
                        />
                      </div>
                    </div>

                    {/* Payment Mode */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Payment Mode</label>
                        <select
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        >
                          <option value="cash">Cash</option>
                          <option value="credit">Credit</option>
                          <option value="cheque">Cheque</option>
                          <option value="btransfer">Bank Transfer</option>
                        </select>
                      </div>
                    </div>

                    {/* UTR/Cheque No */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Cheque/UTR/Cash Receipt No.</label>
                        <input
                          type="text"
                          name="bank"
                          value={formData.bank}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        />
                      </div>
                    </div>

                    {/* Previous Balance */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">
                          Previous Balance
                          {isLoadingBalance && <i className="fas fa-spinner fa-spin ml-2"></i>}
                        </label>
                        <input
                          type="number"
                          name="prebalance"
                          value={formData.prebalance}
                          readOnly
                          className="form-control form-control-sm"
                          placeholder={isLoadingBalance ? "Loading..." : "0"}
                        />
                      </div>
                    </div>

                    {/* Credit */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Credit</label>
                        <input
                          type="number"
                          name="credit"
                          value={formData.credit}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                        />
                      </div>
                    </div>

                    {/* Include VAT Checkbox */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Include VAT</label>
                        <div className="checkbox mt-2">
                          <input
                            type="checkbox"
                            name="includeVat"
                            checked={formData.includeVat}
                            onChange={handleInputChange}
                          />
                          <label className="ml-2">Auto-calculate 7% VAT</label>
                        </div>
                      </div>
                    </div>

                    {/* VAT Amount */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">VAT Amount (7%)</label>
                        <input
                          type="number"
                          name="vat"
                          value={formData.vat}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Discount */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Discount</label>
                        <input
                          type="number"
                          name="Disc"
                          value={formData.Disc}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Debit - Payment Paid */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Debit - Payment Paid</label>
                        <input
                          type="number"
                          name="debit"
                          value={formData.debit}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Balance */}
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Balance</label>
                        <input
                          type="number"
                          name="balance"
                          value={formData.balance}
                          readOnly
                          className="form-control form-control-sm"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Remark */}
                    <div className="col-12">
                      <div className="form-group mb-3">
                        <label className="control-label">Remark</label>
                        <input
                          type="text"
                          name="remark"
                          value={formData.remark}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          placeholder="Enter remarks..."
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              
              <div className="modal-footer sticky-bottom bg-light">
                <button 
                  type="button" 
                  className="btn btn-secondary btn-sm" 
                  onClick={closeEditModal}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary btn-sm"
                  onClick={handleEditSubmit}
                >
                  <i className="fas fa-save mr-2"></i>
                  Update Expense
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary by Subhead Table */}
      {showSummaryTable && (() => {
        const totalPending = summaryData.reduce((sum, item) => sum + item.balance, 0);
        const sortedSubheads = [...summaryData].sort((a, b) => b.balance - a.balance);
        
        return (
          <div className="card mt-3 mb-4" style={{ borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <div className="card-header" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00d4ff 100%)', color: 'white', borderRadius: '10px 10px 0 0' }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-chart-pie mr-2"></i>
                  Pending Balance Summary by Subhead
                  {summaryLoading && <span className="spinner-border spinner-border-sm ml-2" role="status"></span>}
                </h5>
                <div className="d-flex gap-2">
                  <button 
                    type="button" 
                    className="btn btn-sm btn-danger"
                    onClick={exportSummaryToPDF}
                    title="Export to PDF"
                    style={{ borderRadius: '5px', marginRight: '8px' }}
                  >
                    <i className="fas fa-file-pdf mr-1"></i>
                    Export PDF
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-light"
                    onClick={resetSummaryView}
                    title="Reset Summary View"
                    style={{ borderRadius: '5px' }}
                  >
                    <i className="fas fa-redo mr-1"></i>
                    Reset
                  </button>
                </div>
              </div>
            </div>
            
            <div className="card-body" style={{ padding: '20px' }}>
              {sortedSubheads.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#4caf50' }}></i>
                  <p className="mt-3 text-muted">No pending balances found!</p>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle mr-1"></i>
                      Showing all subheads with pending balance greater than ฿0
                    </small>
                  </div>
                  
                  {/* Summary Table */}
                  <div className="table-responsive mb-4">
                    <table className="table table-hover table-bordered">
                      <thead style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00d4ff 100%)', color: 'white' }}>
                        <tr>
                          <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                          <th>Subhead</th>
                          <th style={{ width: '200px', textAlign: 'right' }}>Pending Amount (฿)</th>
                          <th style={{ width: '120px', textAlign: 'center' }}>Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSubheads.map((item, index) => {
                          const percentage = ((item.balance / totalPending) * 100).toFixed(1);
                          return (
                            <tr 
                              key={item.subhead}
                              style={{
                                backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#e3f2fd';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#f8f9fa' : 'white';
                              }}
                            >
                              <td style={{ textAlign: 'center', fontWeight: '600' }}>{index + 1}</td>
                              <td>
                                <i className="fas fa-tag mr-2" style={{ color: '#4facfe' }}></i>
                                <strong>{item.subhead}</strong>
                              </td>
                              <td style={{ textAlign: 'right', color: '#d63031', fontWeight: 'bold' }}>
                                {item.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="badge" style={{ backgroundColor: '#4facfe', color: 'white', fontSize: '12px' }}>
                                  {percentage}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        <tr style={{ background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)', fontWeight: 'bold' }}>
                          <td colSpan="2" style={{ textAlign: 'right', fontSize: '16px', color: '#d63031' }}>
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Total Pending Amount:
                          </td>
                          <td style={{ textAlign: 'right', fontSize: '18px', color: '#d63031' }}>
                            {totalPending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ textAlign: 'center', fontSize: '14px', color: '#d63031' }}>100%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Chart Visualization */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div className="card-header bg-info text-white">
                          <h6 className="mb-0">
                            <i className="fas fa-chart-bar mr-2"></i>
                            Bar Chart - Pending Amounts
                          </h6>
                        </div>
                        <div className="card-body" style={{ height: '300px' }}>
                          <canvas id="summaryBarChart"></canvas>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div className="card-header bg-success text-white">
                          <h6 className="mb-0">
                            <i className="fas fa-chart-pie mr-2"></i>
                            Pie Chart - Distribution
                          </h6>
                        </div>
                        <div className="card-body" style={{ height: '300px' }}>
                          <canvas id="summaryPieChart"></canvas>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      <style jsx>{`
        /* Modal Perfect Centering */
        .expense-modal-backdrop {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 1050 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 20px !important;
          overflow-y: auto !important;
        }
        
        .expense-modal-content {
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-height: calc(100vh - 40px);
          width: 100%;
          display: flex;
          flex-direction: column;
          position: relative;
          background: white;
        }
        
        .modal-dialog {
          margin: 0 !important;
          max-width: 850px;
          width: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .modal-header {
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          flex-shrink: 0;
        }
        
        /* Slim Scrollbar for Modal Body */
        .expense-modal-body {
          max-height: calc(100vh - 180px);
          overflow-y: auto;
          padding: 1rem;
          flex: 1;
        }
        
        /* Enhanced form spacing for 2-column layout */
        .expense-modal-body .row {
          margin-bottom: 0;
        }
        
        .expense-modal-body .form-group {
          margin-bottom: 0.75rem;
          padding: 0 0.5rem;
        }
        
        /* Responsive Form Improvements */
        .expense-modal-body .row {
          row-gap: 0.5rem;
          column-gap: 1rem;
        }
        
        /* Force single column on medium screens and below */
        @media (max-width: 991.98px) {
          .expense-modal-body .col-lg-6 {
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }
        }
        
        @media (max-width: 992px) {
          .expense-modal-body .form-group {
            margin-bottom: 0.75rem;
          }
        }
        
        @media (max-width: 768px) {
          .expense-modal-body .form-group {
            margin-bottom: 0.5rem;
          }
        }
        
        .expense-modal-body::-webkit-scrollbar {
          width: 6px;
        }
        
        .expense-modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .expense-modal-body::-webkit-scrollbar-thumb {
          background: #0AC949;
          border-radius: 3px;
        }
        
        .expense-modal-body::-webkit-scrollbar-thumb:hover {
          background: #08b83f;
        }
        
        /* Firefox scrollbar */
        .expense-modal-body {
          scrollbar-width: thin;
          scrollbar-color: #0AC949 #f1f1f1;
        }
        
        .modal-footer {
          flex-shrink: 0;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
        }
        
        /* Table Action Buttons */
        .table {
          margin-bottom: 0;
        }
        
        .table th {
          background-color: white;
          color: #333;
          font-weight: 600;
          border-color: #dee2e6;
          padding: 12px 8px;
          font-size: 0.875rem;
        }
        
        .table td {
          padding: 10px 8px;
          vertical-align: middle;
          border-color: #dee2e6;
          font-size: 0.875rem;
        }
        
        .table-striped tbody tr:nth-of-type(odd) {
          background-color: rgba(0, 0, 0, 0.02);
        }
        
        .table-hover tbody tr:hover {
          background-color: rgba(10, 201, 73, 0.08);
        }
        
        .d-flex.justify-content-center.align-items-center {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          flex-direction: row !important;
          gap: 8px;
        }
        
        .btn.btn-sm {
          padding: 0.375rem 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          flex-direction: row;
          white-space: nowrap;
        }
        
        .btn-outline-primary {
          color: #007bff;
          border-color: #007bff;
        }
        
        .btn-outline-primary:hover {
          background-color: #007bff;
          border-color: #007bff;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
        }
        
        .btn-outline-danger {
          color: #dc3545;
          border-color: #dc3545;
        }
        
        .btn-outline-danger:hover {
          background-color: #dc3545;
          border-color: #dc3545;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
        }
        
        .font-weight-bold {
          font-weight: 600;
        }
        
        .form-control-sm {
          height: 32px;
          padding: 6px 10px;
          font-size: 13px;
          border-radius: 4px;
          border: 1px solid #ddd;
          transition: all 0.3s ease;
          width: 100%;
        }
        
        .form-control-sm:focus {
          border-color: #0AC949;
          box-shadow: 0 0 0 3px rgba(10, 201, 73, 0.1);
        }
        
        .control-label {
          font-weight: 500;
          color: #555;
          margin-bottom: 4px;
          font-size: 13px;
        }
        
        .form-group {
          margin-bottom: 0.75rem;
        }
        
        .row.g-3 {
          --bs-gutter-x: 0.75rem;
          --bs-gutter-y: 0.5rem;
        }
        
        .row.g-3 > * {
          padding-right: calc(var(--bs-gutter-x) * 0.5);
          padding-left: calc(var(--bs-gutter-x) * 0.5);
          margin-top: var(--bs-gutter-y);
        }
        
        .fa-spinner {
          color: #0AC949;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .ml-2 {
          margin-left: 8px;
        }
        
        .mr-2 {
          margin-right: 8px;
        }
        
        .text-center {
          text-align: center;
        }
        
        .py-4 {
          padding-top: 2rem;
          padding-bottom: 2rem;
        }
        
        .text-muted {
          color: #6c757d;
        }
        
        /* Responsive Design */
        @media (max-width: 1200px) {
          .modal-dialog {
            max-width: 95%;
            margin: 1rem auto;
          }
        }
        
        @media (max-width: 768px) {
          .modal-dialog {
            max-width: 98%;
            margin: 0.5rem auto;
          }
          
          .expense-modal-backdrop {
            padding: 1rem;
          }
          
          .expense-modal-content {
            max-height: calc(100vh - 2rem);
          }
          
          .expense-modal-body {
            max-height: calc(95vh - 120px);
            padding: 1rem;
          }
          
          .col-lg-4 {
            flex: 0 0 50%;
            max-width: 50%;
          }
          
          .form-control-sm {
            height: 40px;
            padding: 10px 12px;
            font-size: 16px; /* Prevent zoom on iOS */
          }
          
          .control-label {
            font-size: 13px;
            margin-bottom: 4px;
          }
          
          .modal-title {
            font-size: 1.1rem;
          }
          
          .btn-sm {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
          }
          
          .expense-modal-backdrop {
            padding: 0.5rem;
          }
        }
        
        @media (max-width: 576px) {
          .expense-modal-body {
            padding: 0.75rem;
          }
          
          .row {
            margin: 0 -8px;
          }
          
          .row > [class*="col-"] {
            padding: 0 8px;
          }
          
          .modal-footer {
            padding: 0.75rem;
            flex-wrap: wrap;
          }
          
          .modal-footer .btn {
            margin: 0.25rem;
            flex: 1;
            min-width: 120px;
          }
        }
        
        /* Sticky header and footer */
        .sticky-top {
          position: sticky;
          top: 0;
          z-index: 1020;
        }
        
        .sticky-bottom {
          position: sticky;
          bottom: 0;
          z-index: 1020;
          border-top: 1px solid #dee2e6;
        }
        
        /* Enhanced form styling */
        .checkbox input[type="checkbox"] {
          transform: scale(1.2);
          margin-right: 8px;
        }
        
        select.form-control-sm {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 8px center;
          background-repeat: no-repeat;
          background-size: 16px 12px;
          padding-right: 32px;
        }
        
        /* Loading state overlay */
        .form-group.loading {
          position: relative;
          opacity: 0.7;
        }
        
        .form-group.loading::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.7);
          pointer-events: none;
        }
      `}</style>
    </Layout>
  );
}
